const STORAGE_KEY = 'cpe-portal-config'
const CONFIG_PATH = '/api/v1/portal/config'

const STATUS = {
  loading: 'loading',
  ready: 'ready',
  notConfigured: 'not_configured',
  unauthorized: 'unauthorized',
  error: 'error',
}

class PortalNotConfiguredError extends Error {
  constructor(message = 'portal_not_configured') {
    super(message)
    this.name = 'PortalNotConfiguredError'
  }
}

const readCache = () => {
  try {
    const cached = JSON.parse(window.localStorage.getItem(STORAGE_KEY))

    return cached && cached.apiUrl ? cached : null
  } catch {
    return null
  }
}

const writeCache = config => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config))
  } catch {}
}

const clearCache = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {}
}

let current = readCache()
let status = current ? STATUS.ready : STATUS.loading
let listeners = []

const notify = () => listeners.forEach(listener => listener(status))

const setStatus = next => {
  status = next
  notify()
}

const subscribe = listener => {
  listeners = [...listeners, listener]

  return () => {
    listeners = listeners.filter(item => item !== listener)
  }
}

const trimSlash = value => String(value || '').replace(/\/+$/, '')

const fallbackConsoleUrl = () =>
  trimSlash(process.env.REACT_APP_CONFIG_URL).replace(
    new RegExp(`${CONFIG_PATH}$`),
    ''
  )

const discoverConsoleUrl = async () => {
  try {
    const response = await fetch(`${process.env.PUBLIC_URL || ''}/portal.json`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })

    if (response.ok) {
      const body = await response.json()

      if (body && body.consoleUrl) return trimSlash(body.consoleUrl)
    }
  } catch {}

  return fallbackConsoleUrl() || null
}

const fetchRemote = async () => {
  const consoleUrl = await discoverConsoleUrl()

  if (!consoleUrl) {
    throw new PortalNotConfiguredError('portal_json_missing')
  }

  // Em produção o console só aceita o header Origin; ?host= serve ao dev local,
  // onde o Origin é localhost.
  const query =
    process.env.NODE_ENV === 'production'
      ? ''
      : `?host=${encodeURIComponent(window.location.hostname)}`

  // no-cache revalida pelo ETag (304) sem forçar preflight de CORS
  const response = await fetch(`${consoleUrl}${CONFIG_PATH}${query}`, {
    cache: 'no-cache',
    headers: { Accept: 'application/json' },
  })

  if (response.status === 404) throw new PortalNotConfiguredError()

  if (!response.ok) {
    throw new Error(`portal config: HTTP ${response.status}`)
  }

  const body = await response.json()

  if (!body || !body.apiUrl) {
    throw new Error('portal config: resposta sem apiUrl')
  }

  return {
    consoleUrl,
    apiUrl: trimSlash(body.apiUrl),
    theme: body.theme || {},
    customer: body.customer || {},
    recaptchaSiteKey: body.recaptchaSiteKey || null,
    version: body.version || null,
  }
}

const reportError = error => {
  // eslint-disable-next-line no-console
  console.error('[portal-config]', error)
}

const revalidate = async () => {
  try {
    const fresh = await fetchRemote()

    const changed =
      !current ||
      fresh.version !== current.version ||
      fresh.consoleUrl !== current.consoleUrl

    if (changed) writeCache(fresh)
  } catch (error) {
    if (error instanceof PortalNotConfiguredError) {
      clearCache()
      return
    }

    reportError(error)
  }
}

let loading = null

const load = () => {
  if (loading) return loading

  if (current) {
    setStatus(STATUS.ready)
    revalidate()
    loading = Promise.resolve(current)

    return loading
  }

  loading = fetchRemote()
    .then(fresh => {
      current = fresh
      writeCache(fresh)
      setStatus(STATUS.ready)

      return current
    })
    .catch(error => {
      if (error instanceof PortalNotConfiguredError) {
        setStatus(STATUS.notConfigured)
      } else {
        reportError(error)
        setStatus(STATUS.error)
      }

      return null
    })

  return loading
}

const markUnauthorized = () => {
  clearCache()
  setStatus(STATUS.unauthorized)
}

const config = () => current
const getStatus = () => status

const portalConfig = {
  STATUS,
  STORAGE_KEY,
  config,
  getStatus,
  load,
  markUnauthorized,
  readCache,
  subscribe,
}

export default portalConfig
